import React, { useMemo, useCallback, useEffect, FC, useState } from "react"
import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import { Map } from "immutable"
import { Loader, ButtonGroup, Button } from "dash-styled"

import { MainThunkDispatch } from "../../../../../../../../../Reducers"
import { AppDetailBase } from "../../../../AppDetailBase/AppDetailBase"
import { useAppInstance } from "../../../../../../../../../Hooks/useAppInstance"
import { useAppTemplate } from "../../../../../../../../../Hooks/useAppTemplate"
import {
    getValue,
    hasResult,
    hasValue
} from "../../../../../../../../../Models/Client/FetchState"
import { SimpleInfoBox } from "../../../../../../../../Common/SimpleInfoBox/SimpleInfoBox"
import {
    findFirstError,
    Result
} from "../../../../../../../../../Models/Utility/Result"
import { useBoolean } from "../../../../../../../../../Hooks/useBoolean"
import { routePaths } from "../../../../../../../../../Routes"
import { useGroundTruthTotalCount } from "../../../../../../../../../Hooks/useGroundTruthTotalCount"
import {
    dirtinessChanged,
    clearInputsForId
} from "../../../../../../../../../Actions/Client/InputRender/InputRenderActions"

import { useJobCount } from "../../../../../../../../../Hooks/useJobCount"
import { APIJobsPerStatusModel } from "../../../../../../../../../Models/API/APIJobsPerStatusModel"
import { hasAppPermission } from "../../../../../../../../../Models/Client/ProjectRole"
import { useAssumedRoles } from "../../../../../../../../../Hooks/useAssumedRoles"
import { useAppState } from "../../../../../../../../../Hooks/useReduxState"
import { useAppStatus } from "../../../../../../../../../Hooks/useAppStatus"
import {
    selectHasFreemiumExpired,
    selectIsSysAdmin
} from "../../../../../../../../../Helpers/Functions/Selectors/auth"
import { useSchemaFormOutput } from "../../../../../../../../../Hooks/useSchemaFormOutput"
import { getParameterSchema } from "../../../../../../../../../Models/API/AppTemplateModel"
import { TabButton } from "../../../../../../../../Common/Button/TabButton"
import { updateSuperTasks } from "../../../../../../../../../Actions/API/ParametersActions"
import {
    updateAppSettingsCustomer,
    updateAppSettingsSysAdmin
} from "../../../../../../../../../Actions/API/AppInstance/AppInstanceActions"
import { ModalView } from "../ModalView"

import { TasksSection } from "./Sections/Tasks"
import { HITLSection } from "./Sections/HITL"
import {
    FormFields,
    formValuesToRestriction,
    getVisibleWorkflows,
    TaskName
} from "./Sections/HITL/FormFields"
import { SuperTaskWorkerModel } from "../../../../../../../../../Models/API/SuperTaskModel"

interface AppCustomizeTaskParametersProps {
    orgUsername: string
    appID: string
}

export const AppCustomizeTaskParameters: FC<AppCustomizeTaskParametersProps> =
    ({ orgUsername, appID }) => {
        const [isRequestingChange, startRequestingChange, endRequestingChange] =
            useBoolean(false)
        const [, , stopLiveValidatingForm] = useBoolean(false)
        const [isSubmitting, startSubmitting, endSubmitting] = useBoolean(false)
        const [
            userExpiryContact,
            showUserExpiryContact,
            hideUserExpiryContact
        ] = useBoolean(false)
        const [saving, startSaving, endSaving] = useBoolean(false)

        const instanceFetch = useAppInstance(appID)

        const templateFetch = useAppTemplate(appID)

        const isSysAdmin = useAppState(selectIsSysAdmin)

        const taskNamesFetch = useMemo(
            () =>
                !hasValue(templateFetch)
                    ? templateFetch
                    : !hasValue(instanceFetch)
                    ? instanceFetch
                    : Result.ok(
                          Object.keys(
                              templateFetch.value.defaultConstraints
                                  .taskConstraints ?? {}
                          ).concat(
                              // for historical reason, special value CANOTIC_OVERRIDE has to
                              // be allowed as the task name if it is already set in the app
                              "CANOTIC_OVERRIDE" in
                                  (instanceFetch.value.constraints
                                      ?.taskConstraints ?? {})
                                  ? ["CANOTIC_OVERRIDE"]
                                  : []
                          )
                      ),
            [instanceFetch, templateFetch]
        )

        const appStatusFetch = useAppStatus(appID)

        const [emailErrors, setEmailErrors] = useState<Map<TaskName, string>>()

        const [jobFormData] = useSchemaFormOutput(
            getValue(instanceFetch)?.appParams?.params
        )

        const dispatch = useDispatch<MainThunkDispatch>()

        const [taskFormData, onTaskFormDataChange] =
            useSchemaFormOutput(undefined)

        const [superTasksSettingsChanged, , , , setSuperTasksChange] =
            useBoolean(false)

        const [HITLFormData, onHITLFormDataChange] =
            useState<FormFields | undefined>(undefined)

        const [HITLSettingsChanged, , , , setHITLChange] = useBoolean(false)

        const rolesFetch = useAssumedRoles(appID)
        const roles = useMemo(() => getValue(rolesFetch) ?? [], [rolesFetch])
        const hasFreemiumPlanExpired = useAppState(selectHasFreemiumExpired)

        const changeParams = useMemo(() => {
            if (hasAppPermission(roles, "task-design:update")) {
                return superTasksSettingsChanged || HITLSettingsChanged
            }
            return false
        }, [roles, superTasksSettingsChanged, HITLSettingsChanged])

        const totalCountFetch = useGroundTruthTotalCount(appID)
        const [
            updateDataPointModalEnabled,
            updateDataPointModalEnabledTrue,
            updateDataPointModalEnabledFalse
        ] = useBoolean(false)
        const [
            showUpdateDataPointModal,
            showUpdateDataPointModalTrue,
            showUpdateDataPointModalFalse
        ] = useBoolean(false)

        const jobCount = useJobCount(appID)
        const totalJobCount = useMemo(
            () => (jobCount ? APIJobsPerStatusModel.total(jobCount) : 0),
            [jobCount]
        )
        const paramsSchemaFetch = useMemo(
            () =>
                hasValue(templateFetch)
                    ? Result.ok(getParameterSchema(templateFetch.value))
                    : undefined,
            [templateFetch]
        )

        useEffect(() => {
            if (totalJobCount !== undefined && totalJobCount > 0) {
                updateDataPointModalEnabledTrue()
            } else {
                updateDataPointModalEnabledFalse()
            }
        }, [
            totalJobCount,
            updateDataPointModalEnabledFalse,
            updateDataPointModalEnabledTrue
        ])

        /**
         * Final update project callback.
         * @param extraParams used in modal submit callback, e.g. cancelAndRelabelJobsInState param can be passed
         * @param callback used as onfulfilled callback, e.g. hides update data point modal
         */
        const handleSubmitTaskSettings = useCallback(
            (extraParams = {}, callback = () => {}) => {
                if (
                    !hasValue(instanceFetch) ||
                    !hasValue(taskNamesFetch) ||
                    !hasValue(templateFetch)
                ) {
                    return
                }
                startSaving()
                Promise.all([
                    hasAppPermission(roles, "task-design:update") &&
                    superTasksSettingsChanged &&
                    taskFormData &&
                    taskFormData?.length > 0
                        ? dispatch(
                              updateSuperTasks({
                                  appID,
                                  superTasks: taskFormData,
                                  ...extraParams
                              })
                          )
                        : undefined,
                    hasAppPermission(roles, "settings:update") &&
                    HITLSettingsChanged &&
                    HITLFormData
                        ? isSysAdmin
                            ? dispatch(
                                  updateAppSettingsSysAdmin(
                                      appID,
                                      HITLFormData.plan.selectedWorkflow,
                                      getVisibleWorkflows(HITLFormData),
                                      formValuesToRestriction(
                                          taskNamesFetch.value,
                                          HITLFormData,
                                          instanceFetch.value.constraints ?? {},
                                          templateFetch.value.defaultConstraints
                                      ),
                                      HITLFormData.whoShouldLabel
                                          .isCanoticCrowd,
                                      extraParams.cancelAndRelabelJobsInState,
                                      HITLFormData.measurement,
                                      HITLFormData.measurerMetrics,
                                      instanceFetch.value.settings
                                  )
                              )
                            : dispatch(
                                  updateAppSettingsCustomer(
                                      appID,
                                      HITLFormData.plan.selectedWorkflow,
                                      HITLFormData.whoShouldLabel
                                          .isCanoticCrowd,
                                      extraParams.cancelAndRelabelJobsInState,
                                      instanceFetch.value.settings
                                  )
                              )
                        : undefined
                ])
                    .catch(() => {})
                    .then(callback)
                    .finally(() => {
                        endSaving()
                        stopLiveValidatingForm()
                    })
            },
            [
                startSaving,
                endSaving,
                stopLiveValidatingForm,
                dispatch,
                appID,
                taskFormData,
                HITLFormData,
                isSysAdmin,
                instanceFetch,
                taskNamesFetch,
                templateFetch,
                roles,
                superTasksSettingsChanged,
                HITLSettingsChanged
            ]
        )

        const handleTriggerSubmit = useCallback(() => {
            if (hasFreemiumPlanExpired) {
                return showUserExpiryContact()
            }

            if (changeParams) {
                if (getValue(totalCountFetch) === 0) {
                    if (updateDataPointModalEnabled) {
                        showUpdateDataPointModalTrue()
                        startSubmitting()
                    } else {
                        handleSubmitTaskSettings()
                    }
                } else {
                    startRequestingChange()
                    startSubmitting()
                }
            }
        }, [
            hasFreemiumPlanExpired,
            changeParams,
            showUserExpiryContact,
            totalCountFetch,
            updateDataPointModalEnabled,
            showUpdateDataPointModalTrue,
            startSubmitting,
            startRequestingChange,
            handleSubmitTaskSettings
        ])

        const switchToJobParameters = useCallback(() => {
            dispatch(
                push(routePaths.appCustomizeJobParameters(orgUsername, appID))
            )
        }, [appID, dispatch, orgUsername])

        const noSave = useMemo(
            () => !changeParams && !isRequestingChange,
            [isRequestingChange, changeParams]
        )

        const showHITLSection = useMemo(() => {
            if (taskFormData == null) return false
            const verifyResultsEntry = taskFormData.find(
                (entry: { superTaskWorkflow: string }) =>
                    entry.superTaskWorkflow ===
                    "general_document_processor.Verify results"
            )
            if (!verifyResultsEntry) {
                return false
            }
            const collaboratorWorker = verifyResultsEntry.workers.find(
                (worker: SuperTaskWorkerModel) =>
                    worker.type === "collaborators" && worker.active
            )
            return collaboratorWorker
        }, [taskFormData])

        const emailErrorsCount = emailErrors?.count() || 0

        const render = () => {
            if (
                !hasResult(instanceFetch) ||
                !hasResult(templateFetch) ||
                !hasValue(paramsSchemaFetch) ||
                !hasValue(taskNamesFetch)
            ) {
                return <Loader />
            }

            if (instanceFetch.isError || templateFetch.isError) {
                return (
                    <SimpleInfoBox variant="alert">
                        Error fetching data.
                        {findFirstError(instanceFetch, templateFetch)}
                    </SimpleInfoBox>
                )
            }

            if (paramsSchemaFetch.value === undefined) return <></>

            return (
                <div
                    className="w-full flex flex-col items-center relative"
                    style={{
                        height: `calc(100% - ${
                            hasValue(appStatusFetch) && appStatusFetch.value
                                ? "204px"
                                : "104px"
                        })`
                    }}
                >
                    <ButtonGroup
                        equalWidth={false}
                        className="w-full h-11 min-h-11 mb-6"
                    >
                        <TabButton
                            active={false}
                            onClick={switchToJobParameters}
                            className="py-2"
                        >
                            Job Settings
                        </TabButton>
                        <TabButton active className="py-2">
                            Task Settings
                        </TabButton>
                    </ButtonGroup>
                    <div className="w-full">
                        {hasAppPermission(roles, "settings:view") &&
                            showHITLSection && (
                                <HITLSection
                                    appID={appID}
                                    taskNamesFetch={taskNamesFetch}
                                    value={HITLFormData}
                                    onChange={onHITLFormDataChange}
                                    setChanged={setHITLChange}
                                    setEmailErrors={setEmailErrors}
                                    onShowUserExpiryContact={
                                        showUserExpiryContact
                                    }
                                    appParams={
                                        getValue(instanceFetch)?.appParams
                                            ?.params
                                    }
                                />
                            )}
                        <TasksSection
                            appID={appID}
                            tasks={taskFormData}
                            onChange={onTaskFormDataChange}
                            setChanged={setSuperTasksChange}
                            disabled={
                                !hasAppPermission(roles, "task-design:update")
                            }
                        />
                    </div>
                    {(hasAppPermission(roles, "task-design:update") ||
                        hasAppPermission(roles, "settings:update")) && (
                        <Button
                            color="darkviolet"
                            variant="contained"
                            onClick={handleTriggerSubmit}
                            disabled={noSave || saving || emailErrorsCount > 0}
                            testId="apply-parameters"
                            className="my-2 w-auto px-12"
                        >
                            {saving ? "Saving..." : "Save changes"}
                        </Button>
                    )}
                </div>
            )
        }

        useEffect(() => {
            if (changeParams !== undefined)
                dispatch(dirtinessChanged(changeParams))
        }, [dispatch, changeParams])

        useEffect(() => {
            return () => {
                dispatch(clearInputsForId(appID))
            }
        }, [dispatch, appID])

        const isDocumentExtraction =
            hasValue(paramsSchemaFetch) &&
            paramsSchemaFetch.value?.uiSchema["ui:field"] ===
                "https://widgets.super.ai/document-extraction-parameter@1"

        return (
            <AppDetailBase
                orgUsername={orgUsername}
                id={appID}
                title="Project Settings"
                breadcrumbs={[]}
                fixedContentWidth={
                    hasValue(paramsSchemaFetch) ? !isDocumentExtraction : true
                }
                render={render}
                renderModalView={() => (
                    <ModalView
                        appID={appID}
                        orgUsername={orgUsername}
                        jobFormData={jobFormData}
                        isSubmitting={isSubmitting}
                        isRequestingChange={isRequestingChange}
                        saving={saving}
                        showUpdateDataPoint={showUpdateDataPointModal}
                        showUpdateDataPointModal={showUpdateDataPointModalTrue}
                        endRequestingChange={endRequestingChange}
                        startSaving={startSaving}
                        endSaving={endSaving}
                        stopLiveValidatingForm={stopLiveValidatingForm}
                        endSubmitting={endSubmitting}
                        hideUpdateDataPointModal={showUpdateDataPointModalFalse}
                        onSubmit={handleSubmitTaskSettings}
                    />
                )}
                forceUserExpiryContact={userExpiryContact}
                resetUserExpiryContact={hideUserExpiryContact}
            />
        )
    }
